import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { Quote, QuoteDetail, QuoteItem } from "@/lib/types"

interface QuoteFilters {
  status?: string
  company_id?: string
  deal_id?: string
  issue_date_from?: string
  issue_date_to?: string
  valid_until_from?: string
  valid_until_to?: string
  sort?: string
}

function buildParams(filters: QuoteFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useQuotes(filters: QuoteFilters = {}) {
  return useQuery({
    queryKey: ["quotes", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<{ data: Quote[] }>(`/api/v1/quotes${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: ["quotes", id],
    queryFn: () => api.get<{ data: QuoteDetail }>(`/api/v1/quotes/${id}`),
    enabled: !!id,
  })
}

export function useCreateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      company_id?: string
      deal_id?: string
      currency?: string
      issue_date?: string
      valid_until?: string
      notes?: string
      terms?: string
      recipient_name?: string
      recipient_email?: string
      owner_id?: string
      items?: Array<{
        item_type?: string
        product_id?: string
        name: string
        description?: string
        quantity?: number
        unit?: string
        unit_price_net?: number
        discount_percent?: number
        vat_rate?: number
      }>
    }) => api.post<{ data: QuoteDetail }>("/api/v1/quotes", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}

export function useUpdateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: QuoteDetail }>(`/api/v1/quotes/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}

export function useDeleteQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/quotes/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}

export function useSendQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: Quote }>(`/api/v1/quotes/${id}/send_quote`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}

export function useAcceptQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: Quote }>(`/api/v1/quotes/${id}/accept`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}

export function useRejectQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: Quote }>(`/api/v1/quotes/${id}/reject`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}

export function useNewQuoteVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: QuoteDetail }>(`/api/v1/quotes/${id}/new_version`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}

export function useUpdateQuoteItems() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: Array<Partial<QuoteItem>> }) =>
      api.put<{ data: QuoteDetail }>(`/api/v1/quotes/${id}/items`, { items }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}

export function useCreateQuoteFromDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dealId: string) => api.post<{ data: QuoteDetail }>("/api/v1/quotes/from_deal", { deal_id: dealId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["quotes"] })
    },
  })
}
